import {
  ActionIcon,
  Text,
  Tooltip,
  Box,
  Flex,
  ThemeIcon,
  Title,
} from "@mantine/core"
import { useHover } from "@mantine/hooks"
import { openConfirmModal } from "@mantine/modals"
import {
  IconChartBarPopular,
  IconLayout,
  IconLock,
  IconLockOpen2,
  IconUserStar,
} from "@tabler/icons-react"
import _ from "lodash"

import { TreeNodeType } from "@costory/types/endpoints/folders"
import { ComponentType, DatePreset, Tag } from "@costory/types/prisma-client"

import { GRID_TEMPLATE } from "@costory/front/components/FileExplorer/FileExplorerHeader"
import BadgeGroup from "@costory/front/components/common/BadgeGroup"
import useCmdClick from "@costory/front/components/common/CmdClick"
import highlightCell from "@costory/front/components/common/HighlightCell"
import CloudIcon from "@costory/front/components/icons/CloudIcons"
import UserAvatar from "@costory/front/components/icons/UserAvatar"
import { useSetDefaultDashboardMutation } from "@costory/front/queries/dashboard"

export const ROW_HEIGHT = 35

export interface FileListItemProps {
  id: string
  type: TreeNodeType
  label: string
  componentType: string
  ownerFullName: string
  ownerId: string
  parentFolderId: string
  tags: Tag[]
  datePreset?: DatePreset | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick: (a: any) => void
  disableHighlightStyle?: boolean
  isDefaultDashboard?: boolean
  disabled?: boolean
}

export const FileListItem = ({
  type,
  id,
  label,
  componentType,
  ownerFullName,
  tags,
  datePreset,
  disableHighlightStyle,
  isDefaultDashboard,
  disabled = false,
}: FileListItemProps) => {
  const tagsWithoutIcon = tags.filter((tag) => !tag.name.startsWith("_icon:"))
  const iconTag = tags
    .find((tag) => tag.name.startsWith("_icon:"))
    ?.name.split(":")[1]
  const { hovered, ref } = useHover()
  const isModifierPressed = useCmdClick()
  const shouldHighlight = hovered && isModifierPressed
  const {
    mutateAsync: setDefaultDashboard,
    isPending: isSetDefaultDashboardMutationPending,
  } = useSetDefaultDashboardMutation()
  const handleClickDefaultDashboard = () => {
    if (!isDefaultDashboard) {
      openConfirmModal({
        title: <Title size="h3">Set dashboard as personal default?</Title>,
        children: (
          <Text size="sm">
            Setting this dashboard as your default will replace the current
            default dashboard.
          </Text>
        ),
        labels: { confirm: "Confirm", cancel: "Cancel" },
        onConfirm: () => setDefaultDashboard(id),
      })
    }
  }

  return (
    <Flex
      display="grid"
      w="100%"
      align="center"
      h={ROW_HEIGHT}
      style={{
        gridTemplateColumns: GRID_TEMPLATE,
        opacity: disabled ? 0.4 : 1,
        pointerEvents: disabled ? "none" : "auto",
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {/* Name cell */}
      <Box
        w="100%"
        display="flex"
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          alignItems: "center",
          gap: 2,
          cursor: "pointer",
        }}
        ref={ref}
      >
        <Box w="20" h="20">
          {type === TreeNodeType.Dashboard ? (
            <IconLayout size={20} strokeWidth={1.5} />
          ) : (
            <IconChartBarPopular size={20} strokeWidth={1.5} />
          )}
        </Box>
        {iconTag && CloudIcon(iconTag, 20)}
        <Tooltip
          label={label}
          multiline
          maw={550}
          style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          <Text
            truncate
            ml={8}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              ...(disableHighlightStyle ? {} : highlightCell(shouldHighlight)),
            }}
          >
            {label}
          </Text>
        </Tooltip>

        {type === TreeNodeType.Dashboard && (
          <Tooltip
            label={
              isDefaultDashboard
                ? "This is your current default dashboard"
                : "Set as default dashboard"
            }
          >
            <ActionIcon
              onClick={handleClickDefaultDashboard}
              color={isDefaultDashboard ? "primary" : "textMuted"}
              disabled={isDefaultDashboard}
              loading={isSetDefaultDashboardMutationPending}
              variant="transparent"
            >
              <IconUserStar size={18} />
            </ActionIcon>
          </Tooltip>
        )}
      </Box>
      {/* Tags cell */}
      <Box
        w="100%"
        display="flex"
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <BadgeGroup
          datePreset={datePreset}
          tags={tagsWithoutIcon}
          maxVisible={3}
        />
      </Box>
      {/* Owner cell */}
      <Box
        w="100%"
        display="flex"
        style={{ justifyContent: "right", alignItems: "center" }}
      >
        <UserAvatar fullName={ownerFullName} />
      </Box>
      {/* Privacy cell */}
      <Box
        w="100%"
        display="flex"
        style={{ justifyContent: "right", alignItems: "center" }}
      >
        <Tooltip label={`This file is ${_.capitalize(componentType)}`}>
          {componentType === ComponentType.PUBLIC ? (
            <ThemeIcon size="sm" variant="subtle" color="primaryHighlight">
              <IconLockOpen2 size={16} />
            </ThemeIcon>
          ) : componentType === ComponentType.PRIVATE ? (
            <ThemeIcon size="sm" variant="subtle" color="textPrimary">
              <IconLock size={16} />
            </ThemeIcon>
          ) : (
            <ThemeIcon size="sm" variant="subtle" color="success">
              <IconLockOpen2 size={16} />
            </ThemeIcon>
          )}
        </Tooltip>
      </Box>
    </Flex>
  )
}
